import { Link, useNavigate } from "react-router";
import type { UsuarioConAccesos } from "@/shared/api";
import { t } from "@/shared/i18n";
import { fechaCorta, fechaHora } from "@/shared/lib/fechas";
import { Chip, Pildora, Tabla, type Columna } from "@/shared/ui";
import { tonoEstado } from "../model/usuarios";
import estilos from "./Usuarios.module.css";

const u = t.usuarios;

export const rutaFicha = (usuarioId: string) => `/usuarios/${usuarioId}`;
export const RUTA_INVITAR = "/usuarios/nuevo";

/** Datos que la ficha de un dado de baja deja escritos en Invitar (en el estado, nunca en la dirección). */
export interface PrellenadoInvitacion {
  readonly correo: string;
  readonly nombre: string;
}

export function prellenadoDe(estado: unknown): PrellenadoInvitacion | null {
  if (estado && typeof estado === "object" && "correo" in estado && "nombre" in estado) {
    const { correo, nombre } = estado;
    if (typeof correo === "string" && typeof nombre === "string") return { correo, nombre };
  }
  return null;
}

export function EstadoUsuario({ estado }: { readonly estado: UsuarioConAccesos["usuario"]["estado"] }) {
  return <Pildora tono={tonoEstado(estado)}>{t.estados[estado]}</Pildora>;
}

/** Lista de usuarios: la primera celda es el enlace a la ficha (el que usa el teclado). */
export function TablaUsuarios({
  usuarios,
  nombres,
}: {
  readonly usuarios: readonly UsuarioConAccesos[];
  readonly nombres: ReadonlyMap<string, string>;
}) {
  const navegar = useNavigate();
  const columnas: Columna<UsuarioConAccesos>[] = [
    {
      clave: "persona",
      titulo: u.columnaPersona,
      orden: ({ usuario }) => usuario.nombre,
      celda: ({ usuario }) => (
        <div className={estilos.persona}>
          <Link to={rutaFicha(usuario.usuarioId)} className={estilos.nombre}>
            {usuario.nombre}
          </Link>
          <span className={estilos.correo}>{usuario.correo}</span>
        </div>
      ),
    },
    {
      clave: "empresas",
      titulo: u.columnaEmpresas,
      rotuloMovil: u.columnaEmpresas,
      orden: ({ accesos }) => accesos.map((a) => a.institucion).join(", "),
      celda: ({ accesos }) =>
        accesos.length === 0 ? (
          <span className={estilos.suave}>{u.sinEmpresas}</span>
        ) : (
          <ul className={estilos.empresas}>
            {accesos.map((a) => (
              <li key={a.institucion}>
                <Chip tono={a.estado === "activo" ? "neutro" : "alerta"}>
                  <span title={nombres.get(a.institucion)}>{a.institucion}</span> · {t.perfiles[a.perfil]}
                </Chip>
              </li>
            ))}
          </ul>
        ),
    },
    {
      clave: "estado",
      titulo: u.columnaEstado,
      rotuloMovil: u.columnaEstado,
      celda: ({ usuario }) => <EstadoUsuario estado={usuario.estado} />,
      orden: ({ usuario }) => t.estados[usuario.estado],
    },
    {
      clave: "ingreso",
      titulo: u.columnaIngreso,
      rotuloMovil: u.columnaIngreso,
      orden: ({ usuario }) => usuario.ultimoIngreso,
      celda: ({ usuario }) =>
        usuario.ultimoIngreso ? (
          fechaHora(usuario.ultimoIngreso)
        ) : (
          <span className={estilos.suave}>{t.general.nunca}</span>
        ),
    },
    {
      clave: "invitado",
      titulo: u.columnaInvitado,
      rotuloMovil: u.columnaInvitado,
      orden: ({ usuario }) => usuario.invitadoEn,
      celda: ({ usuario }) => (
        <span className={estilos.suave}>{u.invitadoPor(usuario.invitadoPor, fechaCorta(usuario.invitadoEn))}</span>
      ),
    },
  ];
  return (
    <Tabla
      columnas={columnas}
      filas={usuarios}
      clave={(x) => x.usuario.usuarioId}
      alNavegar={(x) => void navegar(rutaFicha(x.usuario.usuarioId))}
      textosOrden={t.tabla}
    />
  );
}
